/**
 * CompactEventPill — the BON-142 dense-mode one-line pill for a multi-event
 * calendar day: tree-type dot + BOLD time + venue name, tinted by tree type.
 * Opt-in via {@link WorkshopCalendarPicker}'s `dense` prop — the default
 * `DayCell` keeps rendering the richer `CalendarEventPill` untouched, so the
 * rebook/dashboards calendars are unaffected.
 */
import { dotColor, hexToRgba, venueAndTimeFromEvent } from './calendarEvent';
import type { CalendarEvent } from './calendarEvent';
import { EventBadge } from './EventBadge';

export interface CompactEventPillProps {
  event: CalendarEvent;
  selected: boolean;
  onSelect: (id: string) => void;
}

export function CompactEventPill({ event, selected, onSelect }: CompactEventPillProps) {
  const color = dotColor(event.title);
  const { time, venue } = venueAndTimeFromEvent(event);
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      disabled={event.disabled}
      title={event.disabled ? event.disabledReason : undefined}
      onClick={() => onSelect(event.id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        width: '100%',
        textAlign: 'left',
        padding: '3px 6px',
        marginBottom: 3,
        borderRadius: 5,
        border: `1px solid ${selected ? 'var(--nb-primary)' : 'transparent'}`,
        background: hexToRgba(color, 0.16),
        cursor: event.disabled ? 'not-allowed' : 'pointer',
        opacity: event.disabled ? 0.5 : 1,
        font: 'inherit',
        color: 'var(--nb-ink)',
      }}
    >
      <i
        aria-hidden="true"
        style={{ flex: '0 0 auto', width: 6, height: 6, borderRadius: '50%', background: color }}
      />
      <span style={{ flex: '0 0 auto', fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
        {time}
      </span>
      <span
        style={{
          flex: '1 1 auto',
          minWidth: 0,
          fontSize: 10.5,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {venue}
      </span>
      {event.badge && <EventBadge label={event.badge} />}
    </button>
  );
}
